/**
 * Hours by sede, as the screens need them (migration 0044).
 *
 * A professional's week is a list of blocks: a weekday, a start and an end, and
 * where — a physical sede, that sede and online, or online only. The database
 * refuses a bad week too; these say what is wrong while the week is still being
 * typed, and decide the small questions the booking and moving screens ask.
 */

import { t } from '../i18n';
import { dayKey, weekStartKey, addDaysToKey } from './practiceTime';

/** ISO weekday (1 = Monday) to the key the weekday names are stored under. */
export const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
export const dayKeyOf = (weekday) => DAY_KEYS[weekday - 1];

/** "09:30" or "09:30:00" as minutes since midnight. */
export const minutesOf = (time) => {
  const [hours, minutes] = String(time ?? '').slice(0, 5).split(':').map(Number);
  return hours * 60 + minutes;
};

/** Whole sessions a block holds at this length; one that would run past its end does not count. */
export const sessionsInBlock = (block, sessionMinutes) => {
  const span = minutesOf(block.ends_at) - minutesOf(block.starts_at);
  return span > 0 && sessionMinutes > 0 ? Math.floor(span / sessionMinutes) : 0;
};

/**
 * The first thing wrong with a week, as { weekday, problem }, or null.
 * problem: 'missingTimes' | 'endsBeforeStart' | 'nowhere' | 'overlap'.
 */
export const findProblem = (blocks) => {
  for (const block of blocks) {
    if (!block.starts_at || !block.ends_at) return { weekday: block.weekday, problem: 'missingTimes' };
    if (minutesOf(block.ends_at) <= minutesOf(block.starts_at)) {
      return { weekday: block.weekday, problem: 'endsBeforeStart' };
    }
    if (!block.location_id && !block.allows_online) return { weekday: block.weekday, problem: 'nowhere' };
  }

  // One place at a time: sorted by start, a block that begins before the latest
  // end so far that day overlaps something.
  const sorted = [...blocks].sort((a, b) => a.weekday - b.weekday || minutesOf(a.starts_at) - minutesOf(b.starts_at));
  let latestEnd = null;
  let day = null;
  for (const block of sorted) {
    if (block.weekday !== day) { day = block.weekday; latestEnd = null; }
    if (latestEnd !== null && minutesOf(block.starts_at) < latestEnd) return { weekday: day, problem: 'overlap' };
    latestEnd = Math.max(latestEnd ?? 0, minutesOf(block.ends_at));
  }
  return null;
};

/** Where a free time can be booked: its sede, online, or both. */
export const placeOptions = (slot, onlineLocation) => {
  const options = [];
  if (slot.location_id) options.push({ id: slot.location_id, name: slot.location_name, online: false });
  if (slot.allows_online && onlineLocation) {
    options.push({ id: onlineLocation.id, name: onlineLocation.name, online: true });
  }
  return options;
};

/** A time's place in words: "Sede Champagnat", "Sede Champagnat u online", "Online". */
export const placeLabel = (slot) => {
  if (!slot.location_name) return t('availability.online');
  return slot.allows_online ? t('availability.sedeOrOnline', { sede: slot.location_name }) : slot.location_name;
};

/**
 * Where a moved session goes: it stays online, or in person, when the new time
 * allows that, and otherwise takes what the new time offers. Null when the time
 * offers nowhere.
 */
export const locationForMove = ({ currentOnline, slot, onlineLocation }) => {
  const options = placeOptions(slot, onlineLocation);
  if (!options.length) return null;
  return (options.find((option) => option.online === Boolean(currentOnline)) ?? options[0]).id;
};

/**
 * The sessions a change of modality applies to, out of one patient's upcoming
 * sessions. Only sessions still waiting to happen can change.
 *
 * scope: { kind: 'one', id } | { kind: 'next', count } |
 *        { kind: 'weeks', from, to } — day keys; whole weeks, Monday to Sunday.
 */
export const sessionsInScope = (sessions, scope) => {
  const upcoming = sessions
    .filter((session) => session.status === 'scheduled')
    .sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at));

  if (scope.kind === 'one') return upcoming.filter((session) => session.id === scope.id);
  if (scope.kind === 'next') return upcoming.slice(0, Math.max(0, Math.floor(Number(scope.count) || 0)));
  if (scope.kind === 'weeks' && scope.from && scope.to) {
    const first = weekStartKey(scope.from);
    const last = addDaysToKey(weekStartKey(scope.to), 6);
    return upcoming.filter((session) => {
      const key = dayKey(session.starts_at);
      return key >= first && key <= last;
    });
  }
  return [];
};
